/*
 * (c) Meta Platforms, Inc. and its affiliates.
 */

import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { CgArrowTopRight, CgMenu } from "react-icons/cg";

interface MainNavbarProps {
  position: "fixed" | "absolute" | "relative";
  variant?: "light" | "dark";
}

const MainNavbar = ({ position, variant }: MainNavbarProps) => {
  const { pathname } = useLocation();
  const sectionID = pathname.split("/")[1] || "home";
  const hoverBg =
    variant === "light" ? "hover:bg-info/10" : "hover:bg-secondary";

  return (
    <div
      className={`${position} navbar min-h-0 p-1 z-10 ${
        variant === "light" ? "text-black bg-transparent" : "text-white"
      }`}
    >
      <div className="navbar-start px-4">
        <Link to={"/"} className="font-OptimisticDisplay font-bold">
          <span className="lg:inline whitespace-nowrap text-xl font-bold">
            DollarStreet Factors
          </span>
          <p className="text-xs font-thin text-gray-600">AI Reserach</p>
        </Link>
      </div>
      <div className="navbar-end font-OptimisticDisplay font-bold text-sm">
        <div className="dropdown dropdown-end">
          <label tabIndex={0} className="btn btn-ghost lg:hidden text-lg">
            <CgMenu />
          </label>
          <ul
            tabIndex={0}
            className={`menu menu-compact dropdown-content mt-3 p-2 shadow rounded-box w-52 text-white ${
              variant === "dark" ? "bg-secondary" : "bg-primary"
            }`}
          >
            <li>
              <Link to={"/"}>Explore</Link>
            </li>

            <li>
              <Link to={"/about"}>About</Link>
            </li>

            <li>
              <a
                href="https://github.com/facebookresearch/dollarstreet_factors"
                target="_blank"
                rel="noreferrer"
                className={`${hoverBg} gap-1`}
              >
                Paper <CgArrowTopRight />
              </a>
            </li>

            <li>
              <a
                href="https://github.com/facebookresearch/dollarstreet_factors"
                target="_blank"
                rel="noreferrer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  className="bi bi-github"
                  viewBox="0 0 18 18"
                >
                  <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
                </svg>
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="navbar-end hidden lg:flex text-sm font-OptimisticDisplay font-bold">
        <ul className="menu menu-horizontal menu-compact p-0 my-2">
          <li>
            <NavLink
              to={"/"}
              className={
                sectionID === "home"
                  ? "bg-white text-black rounded-xl"
                  : `${hoverBg} rounded-xl`
              }
            >
              Explore
            </NavLink>
          </li>

          <li className="ml-1">
            <NavLink
              to={"/about"}
              className={
                sectionID === "about"
                  ? "bg-white text-black rounded-xl"
                  : `${hoverBg} rounded-xl`
              }
            >
              About
            </NavLink>
          </li>

          <li className="ml-1">
            <a
              href="https://github.com/facebookresearch/dollarstreet_factors"
              target="_blank"
              rel="noreferrer"
              className={`${hoverBg} gap-1 rounded-xl`}
            >
              Paper <CgArrowTopRight />
            </a>
          </li>

          <li>
            <a
              href="https://github.com/facebookresearch/dollarstreet_factors"
              target="_blank"
              rel="noreferrer"
              className={`${hoverBg} gap-1 rounded-xl`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="bi bi-github"
                viewBox="0 0 18 18"
              >
                <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
              </svg>
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default MainNavbar;
